import * as React from 'react';
import { Validator } from 'core/infrastructure';
import { Binding } from 'core/infrastructure';
import { cloneExcept } from '../../utils';
import { Res } from '../../api';
import BaseComponent from '../BaseComponent';
import * as toastr from 'toastr';
import classnames from 'classnames';

export interface FormComponent {
	getPath(): string;
	showErrors(error: string[]): void;
	getValue(): Result<any>;
}

function isFormComponent(child: any) {
	const typePrototype = child && child.type && child.type.prototype;
	return typePrototype
		&& typeof typePrototype.getPath === 'function'
		&& typeof typePrototype.getValue === 'function'
		&& typeof typePrototype.showErrors === 'function';
}

interface Props<T> extends React.HTMLProps<Form<T>> {
	model: T;
	validator?: Validator<T>;
	small?: boolean;
}

interface State {
	formVersion: number;
	versions: { [path: string]: number };
}

export class Form<T> extends BaseComponent<Props<T>, State> {

	private components: FormComponent[] = [];

	constructor(props: Props<T>) {
		super(props);

		this.state = this.createInitialState(props);
	}

	createInitialState(_props: Props<T>): State {
		return { versions: {}, formVersion: 0 };
	}

	onComponentDidMount() { }
	onComponentWillUnmount() { }

	get model(): T {
		return this.props.model;
	}

	// Este método devuelve una promesa resuelta a modelo
	// cuando el modelo se ha podido actualizar con éxito.
	// Si el modelo no se puede actualizar la promesa NO
	// se resuelve, pero tampoco se rechaza
	ifValid(): Promise<T> {
		// En este caso necesito usar un Promise con el async porque
		// quiero poder NO resolver la promesa
		// eslint-disable-next-line no-async-promise-executor
		return new Promise(async (resolve, _reject) => {
			const valid = await this.updateModel();
			if (valid)
				resolve(this.model);
			else
				toastr.error(Res.Msg.MSG_CURRENT_ERRORS_MUST_BE_SOLVED());
		});
	}

	updateModel(): Promise<boolean> {

		const model = this.model;

		let updated = true;
		this.components.forEach(c => {
			const result = c.getValue();
			if (result.ok)
				Binding.setValue(model, c.getPath(), result.value);
			updated = updated && result.ok;
		});

		return new Promise<boolean>((async (resolve: any, _reject: any) => {

			if (!updated) {
				resolve(false);
				return;
			}

			if (!this.props.validator) {
				resolve(true);
				return;
			}

			const errors = await this.props.validator(model);

			this.components.forEach(c => c.showErrors(errors[c.getPath()] || []));
			const isValid = Object.keys(errors).every(k => errors[k].length === 0);

			resolve(isValid);

		}).bind(this));
	}

	clearErrors(): void {
		this.components.forEach(c => c.showErrors([]));
	}

	private addComponent(component: any) {

		// React llama a esta función con null cuando el componente se
		// desmonta o se actualiza para evitar memory leaks. Como nosotros
		// limpiamos el array entero antes de empezar a guardar referencias,
		// basta con ignorar esa llamada con null.

		// Más info en: https://facebook.github.io/react/docs/more-about-refs.html

		// Me quedo sólo con aquellos componentes que se ajustan a "binders"

		// OJO: No puedo usar isFormComponent porque eso trabaja con React.ReactNode y aquí tengo
		//      componentes ya instanciados
		if (component && component.getValue && component.showErrors && component.getPath)
			this.components.push(component);
	}

	private cloneChildrenWithProps(children: React.ReactNode): React.ReactNode {
		return React.Children.map(children, ((child: React.ReactNode) => {

			// Esto es un poco surrealista. Como el React.isValidElement es una guarda de tipo que
			// hace que child luego valga el tipo pasado a React.isValidElement, necesito detallarlo todo.
			// En realidad, entre esto y que no hiciese nada y luego montara yo el cast debajo, no hay
			// ninguna diferencia.

			type ChildProps = { ref: React.Ref<any>, children: React.ReactNode, linkTo: string };

			if (!React.isValidElement<ChildProps>(child)) {
				return child;
			}

			const newProps: ChildProps & { formId?: string, key?: string, boundValue?: any } = { ...child.props };

			if (isFormComponent(child)) {
				newProps.key = this.getKey(Binding.propertyPathToString(child.props.linkTo));
				newProps.formId = this.props.id;
				newProps.boundValue = Binding.getValue(this.model, Binding.propertyPathToString(child.props.linkTo));
				newProps.ref = (instance: any) => {

					if (typeof child.props.ref === 'function') {
						child.props.ref(instance);
					}
					else if (child.props.ref && typeof child.props.ref === 'object') {
						(child.props.ref as React.RefObject<any>).current = instance;
					}

					// Añadimos el componente a la lista de componentes del padre
					this.addComponent(instance);
				};
			}

			newProps.children = this.cloneChildrenWithProps(child.props.children);

			return React.cloneElement(child, newProps);

		}).bind(this));
	}

	remountAll(): Promise<void> {
		// Devolvemos una promesa por si después de montar de nuevo el Form
		// queremos hacer algo
		return new Promise<void>((resolve, _reject) => {
			this.setState({
				formVersion: this.state.formVersion + 1
			}, () => resolve());
		});
	}

	remount(...propertyPaths: string[]): Promise<void> {

		const versions = this.state.versions;
		propertyPaths.forEach(path => {
			const versions = this.state.versions;
			versions[path] = (versions[path] || 0) + 1;
		});

		// Devolvemos una promesa por si después de montar de nuevo el Form
		// queremos hacer algo
		return new Promise<void>((resolve, _reject) => {
			this.setState({ versions }, () => resolve());
		});
	}

	private getKey(path: string) {
		return `key-${this.state.formVersion}-${path}-${this.state.versions[path] || 0}`;
	}

	render() {
		this.components = [];
		const children = this.cloneChildrenWithProps(this.props.children);

		// Evitamos lanzar el Enter para que no se haga un submit del formulario
		const onKeyPress = (e: React.KeyboardEvent<any>) => {
			if (e.charCode === 13 && (e.target as any).type !== 'textarea') {
				e.preventDefault();
			}
		};

		const className = classnames({
			'form-horizontal': !this.props.small,
			'form-small': this.props.small
		}) + ' ' + (this.props.className || '');

		return <form
			{...cloneExcept(this.props, ['model', 'validator', 'ref', 'key', 'showErrors', 'small'])}
			onKeyPress={onKeyPress}
			className={className} >
			{children}
		</form>;
	}
}

export type FormCtor<T> = new (props: Props<T>) => Form<T>;
